import React, { useState, useEffect } from 'react';
import { connectStore } from 'redux-box';
import { module as wordsinfo } from '@st/wordsinfo';
import { module as ui } from '@st/ui';
import { ThemeButton } from '@sc/custom.styled';
import { uploadFile, getTtsAudio } from 'services/api/file';

const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  return new Blob(byteArrays, { type: contentType });
};

function WordsInfoFormModal({ wordsinfo, ui }) {
  const editData = window.wordsInfoEditData || null;
  const [word, setWord] = useState('');
  const [themes, setThemes] = useState('');
  const [wordGame, setWordGame] = useState(true);
  const [pictionary, setPictionary] = useState(true);
  const [phonetic, setPhonetic] = useState(true);
  const [voiceOver, setVoiceOver] = useState('');
  
  const [fileLoading, setFileLoading] = useState(false);
  const [voiceMode, setVoiceMode] = useState('upload'); // 'upload' or 'generate'

  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (editData) {
      setWord(editData.word || '');
      setThemes(editData.themes || '');
      setWordGame(editData.word_game !== undefined ? editData.word_game : true);
      setPictionary(editData.pictionary !== undefined ? editData.pictionary : true);
      setPhonetic(editData.phonetic !== undefined ? editData.phonetic : true);
      setVoiceOver(editData.voice_over || '');
    } else {
      setWord('');
      setThemes('');
      setWordGame(true);
      setPictionary(true);
      setPhonetic(true);
      setVoiceOver('');
    }
  }, [editData]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileLoading(true);
      uploadFile(file)
        .then((res) => {
          if (res && res.file) {
            setVoiceOver(res.file);
          } else if (res) {
            setVoiceOver(res);
          }
        })
        .catch((err) => {
          console.error("Upload failed:", err);
          alert("Audio upload failed. Please try again.");
        })
        .finally(() => {
          setFileLoading(false);
        });
    }
  };

  const handleGenerateVoice = () => {
    if (!word.trim()) return;
    setGenerating(true);
    getTtsAudio(word.trim())
      .then((res) => {
        if (res && res.success && res.result && res.result.audio_base64) {
          const blob = b64toBlob(res.result.audio_base64, 'audio/mp3');
          const file = new File([blob], 'tts-audio.mp3', { type: 'audio/mp3' });
          return uploadFile(file);
        } else {
          throw new Error('Invalid TTS response');
        }
      })
      .then((uploadRes) => {
        if (uploadRes && uploadRes.file) {
          setVoiceOver(uploadRes.file);
        } else if (uploadRes) {
          setVoiceOver(uploadRes);
        }
      })
      .catch((err) => {
        console.error("Generate Voice failed:", err);
        alert("Failed to generate or upload voice over.");
      })
      .finally(() => {
        setGenerating(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      word,
      themes,
      word_game: wordGame,
      pictionary,
      phonetic,
      voice_over: voiceOver
    };
    if (editData) {
      wordsinfo.updateWord(editData.id, payload);
    } else {
      wordsinfo.createWord(payload);
    }
  };

  return (
    <div style={{ padding: '20px', minWidth: '450px', backgroundColor: '#fff', borderRadius: '8px', maxHeight: '90vh', overflowY: 'auto' }}>
      <h2>{editData ? "Edit Word Info" : "Add Word Info"}</h2>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px' }}>Word Text</label>
          <input
            type="text"
            required
            value={word}
            onChange={e => setWord(e.target.value)}
            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px' }}>Themes (e.g. animals)</label>
          <input
            type="text"
            value={themes}
            onChange={e => setThemes(e.target.value)}
            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input type="checkbox" checked={wordGame} onChange={e => setWordGame(e.target.checked)} />
            Word Game
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input type="checkbox" checked={pictionary} onChange={e => setPictionary(e.target.checked)} />
            Pictionary
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input type="checkbox" checked={phonetic} onChange={e => setPhonetic(e.target.checked)} />
            Phonetic
          </label>
        </div>

        <div style={{ border: '1px solid #eee', padding: '10px', borderRadius: '5px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Voice Over</label>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ marginRight: '15px' }}>
              <input type="radio" checked={voiceMode === 'upload'} onChange={() => setVoiceMode('upload')} /> Upload File
            </label>
            <label>
              <input type="radio" checked={voiceMode === 'generate'} onChange={() => setVoiceMode('generate')} /> Generate Voice Over
            </label>
          </div>
          
          {voiceMode === 'upload' && (
            <div className="sf-file-upload-wrapper" style={{ marginTop: 0 }}>
              <label className="sf-custom-file-upload">
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleFileUpload}
                  disabled={fileLoading || generating}
                />
                {fileLoading ? "Uploading..." : "Upload Audio File"}
              </label>
            </div>
          )}

          {voiceMode === 'generate' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <ThemeButton 
                type="button" 
                onClick={handleGenerateVoice} 
                disabled={generating || fileLoading || !word.trim()}
                style={{ alignSelf: 'flex-start' }}
              >
                {generating ? "Generating & Uploading..." : "Generate Voice Over"}
              </ThemeButton>
            </div>
          )}

          {voiceOver && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'green', wordBreak: 'break-all' }}>
              Current Audio URL: <a href={voiceOver} target="_blank" rel="noreferrer">{voiceOver}</a>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
          <ThemeButton type="button" onClick={() => ui.hideModal()}>Cancel</ThemeButton>
          <ThemeButton primary type="submit" disabled={fileLoading || generating}>Save</ThemeButton>
        </div>
      </form>
    </div>
  );
}

export default connectStore({ wordsinfo, ui })(WordsInfoFormModal);
